<script module lang="ts">
  // The downloadable, prod-configured Helper build, served from the web app's
  // static dir (web-app/public/). Re-copy extension-helper/sideguide-helper.zip
  // here whenever the extension ships a new build.
  export const HELPER_ZIP_URL = '/sideguide-helper.zip';
  // Optional walkthrough video. Leave '' to hide the link; drop a YouTube URL in
  // to show a "Watch the walkthrough" button.
  export const HELPER_VIDEO_URL = 'https://www.youtube.com/watch?v=XCQ00MlTXj8';
</script>

<script lang="ts">
  /**
   * How to install the Sideguide Helper as an unpacked Chrome extension: the download,
   * the steps, and the one warning Chrome shows about it. Shared by the setup modal and
   * the Discovery page's next-step card, which frame it differently. The steps are a
   * real sequence, so they're numbered.
   */
  const STEPS: { title: string; body: string }[] = [
    {
      title: 'Download the Helper',
      body: 'Grab the extension below and unzip it into a folder you’ll keep — Chrome loads it from that folder, so don’t delete it afterwards.',
    },
    {
      title: 'Open Chrome’s extensions page',
      body: 'Paste chrome://extensions into your address bar and press Enter.',
    },
    {
      title: 'Turn on Developer mode',
      body: 'Flip the “Developer mode” switch in the top-right corner.',
    },
    {
      title: 'Load the folder',
      body: 'Click “Load unpacked” and choose the folder you unzipped.',
    },
    {
      title: 'Sign in',
      body: 'Pin the Sideguide Helper, open it, and sign in with your Sideguide account.',
    },
    {
      title: 'Be signed in to Skool',
      body: 'In the same browser, stay signed in to skool.com as the account that posts replies.',
    },
  ];
</script>

<div class="mt-4 flex flex-wrap items-center gap-2">
  <a href={HELPER_ZIP_URL} download class="btn btn-primary btn-sm gap-2">
    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
    Download the Helper (.zip)
  </a>
  {#if HELPER_VIDEO_URL}
    <a href={HELPER_VIDEO_URL} target="_blank" rel="noopener" class="btn btn-ghost btn-sm gap-2">
      <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>
      Watch the walkthrough
    </a>
  {/if}
</div>

<ol class="mt-5 space-y-3.5">
  {#each STEPS as step, i (step.title)}
    <li class="flex gap-3">
      <span class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono text-xs font-semibold text-primary">{i + 1}</span>
      <div class="min-w-0">
        <!-- privacy-ok: a setup WIZARD step's own heading, not community content -->
        <div class="text-sm font-medium">{step.title}</div>
        <p class="mt-0.5 text-sm text-base-content/70">
          {#if step.title === 'Open Chrome’s extensions page'}
            Paste <code class="rounded bg-base-200 px-1.5 py-0.5 font-mono text-xs">chrome://extensions</code> into your address bar and press Enter.
          {:else}
            {step.body}
          {/if}
        </p>
      </div>
    </li>
  {/each}
</ol>

<p class="mt-5 rounded-field border hair bg-base-200/40 px-3.5 py-2.5 text-xs text-base-content/60">
  Chrome may warn about a “developer mode extension” each time it starts — that’s expected for
  an unpacked build and safe to keep. You only download and load it once.
</p>
